// src/components/HeroSection.jsx
import React from 'react';
import { Play, ArrowRight, Sparkles, TrendingUp } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const HeroSection = ({ language, t }) => {
  const isRTL = language === 'ar';
  const [heroRef, heroVisible] = useScrollAnimation(0.1);

  const transactions = [
    { name: 'Coffee Shop', amount: '-$4.50', time: '2 min ago', type: 'expense' },
    { name: 'Salary Deposit', amount: '+$3,200.00', time: '1 hour ago', type: 'income' },
    { name: 'Netflix', amount: '-$15.99', time: '2 days ago', type: 'expense' }
  ];

  const quickActions = [
    { title: 'Send Money', subtitle: 'Quick Transfer', delay: '0.2s' },
    { title: 'Invest', subtitle: 'Portfolio', delay: '0.4s' }
  ];

  const floatingElements = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    icon: i % 4 === 0 ? TrendingUp : Sparkles,
    left: `${10 + (i * 15)}%`,
    top: `${15 + (i * 10)}%`,
    animationDelay: `${i * 0.7}s`,
    animationDuration: `${4 + (i * 0.5)}s`,
    animation: i % 3 === 0 ? 'animate-float' : i % 2 === 0 ? 'animate-pulse' : 'animate-bounce',
    size: i % 4 === 0 ? 'w-4 h-4 lg:w-6 lg:h-6' : 'w-3 h-3 lg:w-4 lg:h-4',
    color: i % 3 === 0 ? 'text-[#2beaf4]' : i % 3 === 1 ? 'text-[#582f8f]' : 'text-[#1b0976]',
    hidden: i >= 4 ? 'hidden lg:block' : ''
  }));

  return (
    <section 
      ref={heroRef}
      id="home" 
      className={`relative min-h-screen flex items-center pt-16 pb-8 overflow-hidden ${
        isRTL ? 'text-right' : 'text-left'
      } bg-gradient-to-br from-[#2beaf4]/10 via-white to-[#582f8f]/15`}
    >
      {/* Enhanced Animated Background Shapes */}
      <div className="absolute top-20 right-10 w-64 sm:w-80 lg:w-96 h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-[#2beaf4]/40 to-[#582f8f]/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse" />
      <div 
        className="absolute bottom-20 left-10 w-80 sm:w-96 lg:w-[500px] h-80 sm:h-96 lg:h-[500px] bg-gradient-to-br from-[#1b0976]/30 to-[#2beaf4]/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse" 
        style={{ animationDelay: '1s' }}
      />
      <div 
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 sm:w-64 lg:w-80 h-48 sm:h-64 lg:h-80 bg-gradient-to-br from-[#582f8f]/30 to-[#1b0976]/20 rounded-full mix-blend-multiply filter blur-2xl opacity-60 animate-pulse" 
        style={{ animationDelay: '2s' }}
      />

      {/* Enhanced Floating Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingElements.map((element) => {
          const IconComponent = element.icon;
          return (
            <div
              key={element.id}
              className={`absolute opacity-30 ${element.animation} ${element.hidden}`}
              style={{
                left: element.left,
                top: element.top,
                animationDelay: element.animationDelay,
                animationDuration: element.animationDuration
              }}
            >
              <IconComponent className={`${element.size} ${element.color}`} />
            </div>
          );
        })}
      </div>

      {/* Main Content Container */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          
          {/* Left Column - Content */}
          <div className={`lg:col-span-6 space-y-6 sm:space-y-8 lg:space-y-10 transition-all duration-1000 ease-out ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            
            {/* Enhanced Platform Badge */}
            <div className={`transition-all duration-1200 delay-100 ease-out ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-[#2beaf4]/15 via-[#582f8f]/10 to-[#1b0976]/15 rounded-full text-xs sm:text-sm font-semibold text-[#1b0976] border-2 border-[#2beaf4]/30 shadow-lg shadow-[#2beaf4]/20">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-[#582f8f]" />
                  UNDER ESTABLISHMENT
                <div className="ml-2 w-2 h-2 bg-[#2beaf4] rounded-full animate-pulse"></div>
              </div>
            </div>

            {/* Enhanced Main Headlines */}
            <div className="space-y-4 sm:space-y-6">
              <div className={`transition-all duration-1200 delay-200 ease-out ${
                heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}>
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight">
                  <span className="block bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] bg-clip-text text-transparent">
                    Banking
                  </span>
                  <span className="block mt-1 sm:mt-2 text-[#1b0976]">
                    Made{' '}
                    <span className="relative bg-gradient-to-r from-[#582f8f] via-[#2beaf4] to-[#1b0976] bg-clip-text text-transparent">
                      Simple
                      <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                    </span>
                  </span>
                </h1>
              </div>
              
              <div className={`transition-all duration-1200 delay-400 ease-out ${
                heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}>
                <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl bg-gradient-to-r from-[#582f8f] to-[#1b0976] bg-clip-text text-transparent font-semibold mb-3 sm:mb-4">
                  {t.hero.subtitle}
                </p>
                <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-[#1b0976]/80 leading-relaxed max-w-2xl">
                  {t.hero.description}
                </p>
              </div>
            </div>
            
            {/* Enhanced Call-to-Action Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 lg:gap-6 transition-all duration-1200 delay-600 ease-out ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              {/* Primary Button with Enhanced Gradient */}
              <button className="group relative inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 lg:px-10 lg:py-5 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] text-white rounded-xl lg:rounded-2xl hover:from-[#1b0976] hover:via-[#582f8f] hover:to-[#2beaf4] transition-all duration-300 font-semibold text-base sm:text-lg lg:text-xl shadow-2xl shadow-[#2beaf4]/30 hover:shadow-3xl hover:shadow-[#582f8f]/40 transform hover:-translate-y-2 hover:scale-105 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#2beaf4] via-[#582f8f] to-[#1b0976] opacity-0 group-hover:opacity-75 transition-opacity duration-300 blur-xl" />
                <span className="relative z-10 flex items-center">
                  {t.hero.cta}
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 transition-all duration-300 group-hover:translate-x-2 group-hover:scale-110 ${
                    isRTL ? 'mr-2 sm:mr-3' : 'ml-2 sm:ml-3'
                  }`} />
                </span>
                <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shimmer" />
              </button>

              {/* Secondary Button with Makeen Colors */}
              <button className="group inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 lg:px-10 lg:py-5 bg-white/90 backdrop-blur-sm text-[#1b0976] rounded-xl lg:rounded-2xl hover:bg-gradient-to-r hover:from-[#2beaf4]/10 hover:to-[#582f8f]/10 transition-all duration-300 font-semibold text-base sm:text-lg lg:text-xl border-2 border-[#2beaf4]/40 shadow-xl shadow-[#2beaf4]/20 hover:shadow-2xl hover:shadow-[#582f8f]/30 transform hover:-translate-y-1">
                <Play className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-[#582f8f] transition-all duration-300 group-hover:scale-110 group-hover:text-[#2beaf4] ${
                  isRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'
                }`} />
                {t.hero.watchDemo}
              </button>
            </div>

            {/* Enhanced Trust Indicators */}
            <div className={`hidden sm:flex items-center space-x-6 lg:space-x-8 transition-all duration-1200 delay-800 ease-out ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              {/* <div className="flex items-center space-x-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-[#2beaf4] rounded-full animate-pulse" />
                <span className="text-xs lg:text-sm font-medium text-[#1b0976]/80">Under Establishment</span>
              </div> */}
              {/* <div className="w-px h-3 lg:h-4 bg-[#582f8f]/40" /> */}
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-[#582f8f] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                <span className="text-xs lg:text-sm font-medium text-[#1b0976]/80">Bank-Level Security</span>
              </div>
              <div className="w-px h-3 lg:h-4 bg-[#582f8f]/40" />
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-[#1b0976] rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                <span className="text-xs lg:text-sm font-medium text-[#1b0976]/80">5-min Setup</span>
              </div>
            </div>
          </div>

          {/* Right Column - Enhanced Phone Mockup */}
          <div className="lg:col-span-6 flex justify-center lg:justify-end mt-8 lg:mt-0">
            <div className={`relative transition-all duration-1500 delay-700 ease-out ${
              heroVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
            }`}>
              
              {/* Floating Animation Container */}
              <div className="animate-float">
                {/* Phone Container */}
                <div className="relative w-72 h-[450px] sm:w-80 sm:h-[500px] md:w-96 md:h-[600px] lg:w-[420px] lg:h-[650px] xl:w-[450px] xl:h-[700px]">
                  
                  {/* Enhanced Phone Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2beaf4]/40 via-[#582f8f]/30 to-[#1b0976]/40 rounded-[2rem] lg:rounded-[3rem] blur-2xl lg:blur-3xl scale-110 animate-pulse" />
                  
                  {/* Phone Device Frame */}
                  <div className="relative w-full h-full bg-gradient-to-br from-[#1b0976] to-[#582f8f] rounded-[2rem] lg:rounded-[3rem] shadow-2xl shadow-[#1b0976]/30 overflow-hidden transform hover:scale-105 transition-all duration-500 border-4 lg:border-8 border-[#582f8f]/50">
                    
                    {/* Phone Screen Content with Makeen Colors */}
                    <div className="relative h-full bg-gradient-to-br from-[#2beaf4] via-[#582f8f] to-[#1b0976] p-4 sm:p-6 lg:p-8 flex flex-col">
                      
                      {/* Status Bar & Balance */}
                      <div className="text-white space-y-3 sm:space-y-4 lg:space-y-6 animate-fadeInDown">
                        {/* Status Bar */}
                        <div className="flex items-center justify-between">
                          <div className="text-xs sm:text-sm opacity-90">Good morning, Alex</div>
                          <div className="flex items-center space-x-1">
                            <div className="w-1 h-1 bg-white rounded-full" />
                            <div className="w-1 h-1 bg-white rounded-full" />
                            <div className="w-1 h-1 bg-white rounded-full" />
                            <div className="text-xs sm:text-sm opacity-90">09:41</div>
                          </div>
                        </div>
                        
                        {/* Balance Section */}
                        <div className="space-y-2 lg:space-y-3">
                          <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold animate-pulse">
                            $24,850.00
                          </div>
                          <div className="text-xs sm:text-sm opacity-90">Available Balance</div>
                          <div className="flex items-center space-x-2">
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-300" />
                            <span className="text-xs sm:text-sm text-green-300">+12.5% this month</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Quick Actions Grid */}
                      <div className="mt-4 sm:mt-6 lg:mt-8 grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                        {quickActions.map((action, index) => (
                          <div 
                            key={index}
                            className="bg-white/20 backdrop-blur-sm rounded-lg lg:rounded-2xl p-2 sm:p-3 lg:p-4 hover:bg-white/30 transition-all duration-300 transform hover:scale-105 animate-slideInRight border border-white/10" 
                            style={{ animationDelay: action.delay }}
                          >
                            <div className="text-white">
                              <div className="text-xs opacity-90">{action.title}</div>
                              <div className="text-sm lg:text-lg font-semibold">{action.subtitle}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Transaction History */}
                      <div className="mt-4 sm:mt-6 lg:mt-8 space-y-2 sm:space-y-3 lg:space-y-4 flex-1">
                        <div className="text-white text-sm sm:text-base lg:text-lg font-semibold mb-2 sm:mb-3 lg:mb-4">
                          Recent Activity
                        </div>
                        
                        {transactions.map((transaction, index) => (
                          <div 
                            key={index}
                            className="bg-white/15 backdrop-blur-sm rounded-lg lg:rounded-xl p-2 sm:p-3 lg:p-4 hover:bg-white/25 transition-all duration-300 transform hover:scale-105 animate-slideInRight border border-white/10" 
                            style={{ animationDelay: `${0.6 + (index * 0.2)}s` }}
                          >
                            <div className="flex items-center justify-between text-white">
                              <div>
                                <div className="text-xs sm:text-sm font-medium">{transaction.name}</div>
                                <div className="text-xs opacity-75">{transaction.time}</div>
                              </div>
                              <div className={`text-xs sm:text-sm font-semibold ${
                                transaction.type === 'income' ? 'text-green-300' : 'text-white'
                              }`}>
                                {transaction.amount}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Decorative Floating Elements */}
              <div className="absolute -top-3 sm:-top-4 lg:-top-6 -right-3 sm:-right-4 lg:-right-6 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-[#2beaf4] rounded-full opacity-60 animate-ping shadow-lg shadow-[#2beaf4]/30" />
              <div className="absolute -bottom-4 sm:-bottom-6 lg:-bottom-8 -left-4 sm:-left-6 lg:-left-8 w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-[#582f8f] rounded-full opacity-40 animate-pulse shadow-lg shadow-[#582f8f]/30" />
              <div className="absolute top-1/4 -left-6 sm:-left-8 lg:-left-12 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-[#1b0976] rounded-full opacity-50 animate-bounce shadow-lg shadow-[#1b0976]/30" />
              <div 
                className="absolute top-3/4 -right-6 sm:-right-8 lg:-right-10 w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-[#2beaf4] rounded-full opacity-50 animate-pulse shadow-lg shadow-[#2beaf4]/30" 
                style={{ animationDelay: '1s' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;